import { Badge, Stack, Typography, WithTooltip } from '@compass/components'
import { cn } from '@compass/utils'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import { BarRounded } from '@visx/shape'
import { AppRoute } from 'appRoutes'
import { LinkButtonLink } from 'components/compass-integration'
import { useCurrentSubscription, useCurrentSubscriptionData } from 'features/subscription'
import { DEFAULT_STORED_DATA_TTL_DAYS, getEarliestAllowedDate } from 'helpers/dateRange'
import { formatNum } from 'helpers/format'
import { useLDFlag } from 'hooks/launchDarkly'
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { IdEventV4 } from 'models/event'
import { Fragment, PropsWithChildren, ReactNode, useMemo } from 'react'

import { calculateSuspectScoreDisplayProps, SuspectScoreDisplayProps } from './calculateSuspectScoreDisplayProps'
import { ResponsiveSimpleBarChart } from './SimpleBarChart'
import { SuspectScoreBreakdownPopover } from './SuspectScoreBreakdownPopover'
import { SuspectScoreIndicator } from './SuspectScoreIndicator'
import { useSuspectScoreQuantilesQuery } from './useSuspectScoreQuantiles'

export function SmartSignalHighlights({ event }: { event?: IdEventV4 }) {
  if (event != null && (event.velocity == null || event.velocity?.events == null || event.suspect_score == null)) {
    return null
  }

  return (
    <Stack direction={{ default: 'column', sm: 'row' }} gap={4}>
      <Paper className='p-6 min-w-0 w-full flex flex-col gap-4 justify-between'>
        <SuspectScore event={event} />
      </Paper>
      <Paper className='p-6 min-w-0 w-full flex flex-col gap-4 justify-between'>
        <VelocitySignals event={event} />
      </Paper>
    </Stack>
  )
}

// To be used once the third column is implemented.
export function Wrapper({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn(['p-6 grow shrink-0', 'flex flex-col gap-3 lg:justify-between', className])}>{children}</div>
  )
}

function SuspectScore({ event }: { event?: IdEventV4 }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const score = event?.suspect_score
  const isBreakdownEnabled = useLDFlag('dashboard.suspect-score-breakdown.enabled', false)

  const { data: quantiles } = useSuspectScoreQuantilesQuery(
    subscriptionId,
    DateTime.fromMillis(event?.timestamp ?? Date.now())
  )

  const displayProps = useMemo(() => calculateSuspectScoreDisplayProps(score, quantiles), [quantiles, score])

  return (
    <>
      <Stack direction='column' gap={3}>
        <Stack direction='column' gap={1}>
          <Stack direction='row' gap={1} className='items-center'>
            {isBreakdownEnabled ? (
              <SuspectScoreBreakdownPopover event={event}>
                <Typography
                  as='span'
                  size='base'
                  weight='medium'
                  decoration='underline dotted'
                  className='text-gray-900'
                >
                  Suspect Score
                </Typography>
              </SuspectScoreBreakdownPopover>
            ) : (
              <>
                <Typography size='base' weight='medium' className='text-gray-900'>
                  Suspect Score
                </Typography>
                <WithTooltip content='Value representing how many Smart Signals indicative of suspicious or fraudulent activity were detected on this event.'>
                  <InfoIcon size={12} className='text-gray-800' />
                </WithTooltip>
              </>
            )}
          </Stack>

          <Stack direction='row' gap={2} className='items-center'>
            {score == null ? (
              <Skeleton width={88} height={20} className='my-1 transform-none' />
            ) : (
              <>
                <Typography size='xl' weight='medium'>
                  {score}
                </Typography>
                <SuspectScoreBadge activeLine={displayProps.activeLine} />
              </>
            )}
          </Stack>
        </Stack>

        <SuspectScoreIndicator displayProps={displayProps} />
      </Stack>

      {displayProps?.footerText ? (
        <Typography size='xs' className='mt-2 text-gray-800'>
          {displayProps.footerText}
        </Typography>
      ) : (
        <Skeleton className='mt-2 h-4 w-5/6 sm:h-8 sm:w-full transform-none' />
      )}
    </>
  )
}

function SuspectScoreBadge({ activeLine }: Pick<SuspectScoreDisplayProps, 'activeLine'>) {
  if (activeLine == null) {
    return <Skeleton width={48} height={14} className='transform-none!' />
  }

  switch (activeLine) {
    case 'green':
      return (
        <Badge variant='solid' tone='success'>
          Low
        </Badge>
      )

    case 'yellow':
      return (
        <Badge variant='solid' tone='warning'>
          Medium
        </Badge>
      )

    default:
      return (
        <Badge variant='solid' tone='danger'>
          High
        </Badge>
      )
  }
}

function VelocityKey({ tooltipContent, children }: { tooltipContent?: ReactNode; children?: ReactNode }) {
  return (
    <div>
      <WithTooltip content={tooltipContent} className='text-start'>
        <Typography
          size='sm'
          className='text-gray-800 underline underline-offset-2 decoration-1 decoration-dotted cursor-help'
        >
          {children}
        </Typography>
      </WithTooltip>
    </div>
  )
}

function VelocitySignals({ event }: { event?: IdEventV4 }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const { subscription } = useCurrentSubscriptionData()
  const storedDataTtlDays = subscription?.storedDataTtl ?? DEFAULT_STORED_DATA_TTL_DAYS
  const velocity = event?.velocity
  const ipInfo = useMemo(() => event?.ip_info, [event?.ip_info])

  if (event != null && velocity == null) {
    return <p>Velocity signals not available.</p>
  }

  // For high-activity visitors exceeding a count of 20,000 within a 24-hour window, we will omit the aggregation of 24-hour results.
  const ipAddresses = velocity?.distinct_ip == null ? undefined : (velocity?.distinct_ip?.['24_hours'] ?? 20_001)
  const countries = velocity?.distinct_country?.['24_hours']
  const eventsPerIp = velocity?.ip_events?.['24_hours']

  const ipV4 = ipInfo?.v4?.address
  const ipV6 = ipInfo?.v6?.address
  const piiRedacted =
    event?.ip_info?.v4?.address === '0.0.0.0' ||
    event?.ip_info?.v6?.address === '0.0.0.0' ||
    event?.ip_address === '0.0.0.0'

  const eventTimestamp = event?.timestamp
  const eventDateTime = eventTimestamp ? DateTime.fromMillis(eventTimestamp) : undefined
  const since = eventDateTime?.minus({ hours: 24 })?.toISO() ?? undefined
  const before = eventDateTime?.plus({ millisecond: 1 })?.toISO() ?? undefined

  const EventsPerIpWrapper =
    eventsPerIp != null &&
    eventsPerIp > 1 &&
    eventDateTime != null &&
    eventDateTime.minus({ hours: 24 }) >= getEarliestAllowedDate(storedDataTtlDays) // data retention cut-off
      ? ({ children }: { children: ReactNode }) => (
          <LinkButtonLink
            to={AppRoute.IdentificationEvents}
            params={{ subscriptionId }}
            search={{ ipAddress: ipV6 ?? ipV4, since, before }}
            size='sm'
            variant='ghost'
            className='grid grid-cols-subgrid col-span-2 -m-2'
          >
            {children}
          </LinkButtonLink>
        )
      : Fragment

  return (
    <>
      <Stack direction='column' gap={2}>
        <Stack direction='column' gap={1}>
          <Stack direction='row' gap={1} className='items-center'>
            <Typography size='base' weight='medium' className='text-gray-900'>
              Velocity Signals
            </Typography>
            <WithTooltip content='Key data points indicating suspicious activity during the 24 hours prior to this event.'>
              <InfoIcon size={12} className='text-gray-800' />
            </WithTooltip>
          </Stack>

          <div className='grid grid-cols-[auto_max-content_max-content] grid-rows-3 gap-x-2 items-center'>
            <VelocityKey
              tooltipContent={
                <div>
                  Number of events associated with the IP address
                  {piiRedacted ? null : (
                    <>
                      :
                      {ipV4 ? (
                        <>
                          <br />
                          <span className='font-mono text-gray-600'>{ipV4}</span>
                        </>
                      ) : null}
                      {ipV6 ? (
                        <>
                          <br />
                          <span className='font-mono text-gray-600'>{ipV6}</span>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              }
            >
              Events per IP
            </VelocityKey>
            <EventsPerIpWrapper>
              {velocity ? (
                <span
                  className={cn('text-end tabular-nums', {
                    'text-lg font-medium': eventsPerIp != null,
                  })}
                >
                  {eventsPerIp != null ? formatNum(eventsPerIp) : 'N/A'}
                </span>
              ) : (
                <span />
              )}
              <Typography size='base' className='text-gray-800'>
                {event ? (
                  <VelocityIndicator count={eventsPerIp} />
                ) : (
                  <Skeleton width={32} height={16} className='transform-none my-1' />
                )}
              </Typography>
            </EventsPerIpWrapper>

            <VelocityKey
              tooltipContent={
                <div>
                  Number of distinct IP addresses associated with the visitor ID:
                  <br />
                  <span className='font-mono text-gray-600'>{event?.identification?.visitor_id}</span>
                </div>
              }
            >
              IP addresses
            </VelocityKey>
            {velocity ? (
              <span
                className={cn('text-end tabular-nums', {
                  'text-lg font-medium': ipAddresses != null,
                })}
              >
                {ipAddresses != null ? (ipAddresses > 20_000 ? formatNum(20000) + '+' : formatNum(ipAddresses)) : 'N/A'}
              </span>
            ) : (
              <span />
            )}
            <Typography size='base' className='text-gray-800'>
              {event ? (
                <VelocityIndicator count={ipAddresses} />
              ) : (
                <Skeleton width={32} height={16} className='transform-none my-1' />
              )}
            </Typography>

            <VelocityKey
              tooltipContent={
                <div>
                  Number of distinct countries associated with the visitor ID:
                  <br />
                  <span className='font-mono text-gray-600'>{event?.identification?.visitor_id}</span>
                </div>
              }
            >
              Countries
            </VelocityKey>
            {velocity ? (
              <span
                className={cn('text-end tabular-nums', {
                  'text-lg font-medium': countries != null,
                })}
              >
                {countries != null ? formatNum(countries) : 'N/A'}
              </span>
            ) : (
              <span />
            )}
            <Typography size='base' className='text-gray-800'>
              {event ? (
                <VelocityIndicator count={countries} />
              ) : (
                <Skeleton width={32} height={16} className='transform-none my-1' />
              )}
            </Typography>
          </div>
        </Stack>
      </Stack>

      <Typography size='xs' className='text-gray-800'>
        Distinct values seen for this visitor in the 24h before this event.
      </Typography>
    </>
  )
}

function VelocityIndicator({ count }: { count?: number }) {
  if (count == null) {
    return null
  }
  switch (count) {
    case 0:
    case 1:
      return <CircleCheckIcon size={16} className='text-blue-800!' />

    case 2:
      return <CircleAlertIcon size={16} className='text-gray-800!' />

    default:
      return <TriangleAlertIcon size={16} className='text-red-800!' />
  }
}

// To be used once the third column is implemented.
export function PreviousActivity({ event }: { event?: IdEventV4 }) {
  const activityLog = useMemo(
    () =>
      Array.from({ length: 24 }, (_, index) => ({
        timestamp: DateTime.now().startOf('hour').minus({ hours: index }),
        value: Math.round(Math.random() * 20),
      })).toReversed(),
    []
  )

  const xDomain = activityLog.map((a) => a.timestamp.valueOf())
  const yDomain = [0, Math.max(3, ...activityLog.map((a) => a.value))]

  if (event == null) {
    return <Skeleton />
  }

  return (
    <>
      <Stack direction='column' gap={2}>
        <Stack direction='column' gap={1}>
          <Stack direction='row' gap={2} className='items-center'>
            <Typography size='base' weight='medium'>
              Previous activity
            </Typography>
            <WithTooltip content='Activity from this visitor during the 24 hours prior to this event.'>
              <InfoIcon size={12} className='text-gray-800' />
            </WithTooltip>
          </Stack>

          <Stack direction='row' gap={2} className='items-center'>
            <Typography size='lg' weight='medium'>
              {0}
            </Typography>
            <Badge variant='solid' tone='danger'>
              High
            </Badge>
          </Stack>
        </Stack>

        <div className='min-w-0 h-6 flex flex-col items-end'>
          <ResponsiveSimpleBarChart xDomain={xDomain} yDomain={yDomain}>
            {({ xScale, yScale, yMax }) => (
              <>
                {activityLog.map((p) => {
                  const barWidth = xScale.bandwidth()
                  const barHeight = yMax - (yScale(p.value) ?? 0)
                  const barX = xScale(p.timestamp.valueOf())
                  const barY = yMax - barHeight
                  return (
                    <Fragment key={`bar-${p.timestamp}`}>
                      <rect
                        x={barX}
                        y={yMax - 1}
                        width={barWidth}
                        height={1}
                        fill={'hsl(var(--fpds-color-orange-7))'}
                      />
                      <BarRounded
                        x={barX ?? 0}
                        y={barY}
                        width={barWidth}
                        height={barHeight}
                        fill='hsl(var(--fpds-color-orange-7))'
                        radius={2}
                        top
                      />
                    </Fragment>
                  )
                })}
              </>
            )}
          </ResponsiveSimpleBarChart>
        </div>
      </Stack>

      <Typography size='xs' className='text-gray-800'>
        There were 12 requests in the 24h before this event.
      </Typography>
    </>
  )
}
